import { useState, useEffect, useCallback } from 'react';

/**
 * React hook for authentication state.
 *
 * Options:
 *   - authPath: string (default: '/api/auth') — base path for auth endpoints
 *
 * Returns:
 *   - user: object | null
 *   - loading: boolean
 *   - error: string | null
 *   - login: (credential) => Promise<void>
 *   - logout: () => Promise<void>
 */
export function useAuth({ authPath = '/api/auth' } = {}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check existing session on mount
  useEffect(() => {
    fetch(`${authPath}/me`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setUser(data.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [authPath]);

  // Login with Google credential
  const login = useCallback(async (credential) => {
    setError(null);
    try {
      const res = await fetch(`${authPath}/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Authentication failed');
        return;
      }
      setUser(data.user);
    } catch (err) {
      setError('Authentication failed. Please try again.');
    }
  }, [authPath]);

  // Logout
  const logout = useCallback(async () => {
    await fetch(`${authPath}/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setUser(null);
  }, [authPath]);

  return { user, loading, error, login, logout };
}
